<template>
  <div class="py-4 container-fluid">
    <div class="card">
      <div class="card-header pb-0">
        <div class="d-flex align-items-center mb-3" :class="{ 'flex-column align-items-stretch': isMobileDevice }">
          <h6 class="mb-0" :class="{ 'w-100 mb-2': isMobileDevice }">Órdenes</h6>
          <div class="d-flex align-items-center" :class="{ 'w-100': isMobileDevice, 'ms-auto': !isMobileDevice }" style="gap: 10px">
            <button
              v-if="isMobileDevice"
              type="button"
              class="btn btn-warning btn-sm"
              :class="{ 'w-100': isMobileDevice }"
              @click="toggleScanner"
            >
              <i class="fas fa-camera"></i>
              {{ isScanning ? '' : '' }}
            </button>

            <!-- Botones individuales solo en desktop -->
            <template v-if="!isMobileDevice">
              <button
                type="button"
                class="btn btn-info btn-sm"
                :disabled="!selectedOrders.length || isPrintingPickingList"
                @click="printPickingList"
              >
                <span v-if="isPrintingPickingList">
                  <i class="fas fa-spinner fa-spin me-1"></i> Generando...
                </span>
                <span v-else>
                  <i class="fas fa-print me-1"></i>
                  Imprimir seleccionados{{ selectedOrders.length ? ` (${selectedOrders.length})` : '' }}
                </span>
              </button>
              <button
                type="button"
                class="btn btn-success btn-sm"
                :disabled="!selectedOrders.length"
                @click="openBarcodeModal"
              >
                <i class="fas fa-barcode me-1"></i>
                Generar códigos{{ selectedOrders.length ? ` (${selectedOrders.length})` : '' }}
              </button>
              <button
                type="button"
                class="btn btn-primary btn-sm"
                :disabled="isSyncing"
                @click="syncOrders"
              >
                <span v-if="isSyncing">
                  <i class="fas fa-spinner fa-spin me-1"></i>
                  Sincronizando{{ syncProgress.total > 0 ? ` (${syncProgress.total})` : '...' }}
                </span>
                <span v-else>Sincronizar</span>
              </button>
            </template>
          </div>
        </div>
        <div class="row">
          <div class="col-md-3 mb-3">
            <label for="searchQuery" class="mb-1">Buscar</label>
            <input
              id="searchQuery"
              v-model="searchQuery"
              type="text"
              class="form-control"
              placeholder="Buscar por orden, cliente, email o ID..."
              @input="onSearchInput"
              @keyup.enter="onSearchEnter"
            />
          </div>
          <div class="col-md-2 mb-3">
            <label for="dateFromFilter" class="mb-1">Desde</label>
            <input
              id="dateFromFilter"
              v-model="dateFromFilter"
              type="date"
              class="form-control"
              @change="fetchOrders(1)"
            />
          </div>
          <div class="col-md-2 mb-3">
            <label for="dateToFilter" class="mb-1">Hasta</label>
            <input
              id="dateToFilter"
              v-model="dateToFilter"
              type="date"
              class="form-control"
              @change="fetchOrders(1)"
            />
          </div>
          <div class="col-md-2 mb-3">
            <label for="statusFilter" class="mb-1">Estado</label>
            <select
              id="statusFilter"
              v-model="statusFilter"
              class="form-select"
              @change="fetchOrders(1)"
            >
              <option value="">Todos</option>
              <option
                v-for="status in statusOptions"
                :key="status"
                :value="status"
              >
                {{ status }}
              </option>
            </select>
          </div>
          <div class="col-md-2 mb-3">
            <label for="paymentStatusFilter" class="mb-1">Pago</label>
            <select
              id="paymentStatusFilter"
              v-model="paymentStatusFilter"
              class="form-select"
              @change="fetchOrders(1)"
            >
              <option value="">Todos</option>
              <option value="paid">Pagado</option>
              <option value="not_paid">No pagado</option>
            </select>
          </div>
          <div class="col-md-1 mb-3 d-flex align-items-end">
            <button
              type="button"
              class="btn btn-outline-secondary btn-sm w-100 mb-1"
              @click="clearFilters"
              title="Limpiar filtros"
            >
              <i class="fas fa-eraser"></i>
            </button>
          </div>
        </div>
      </div>
      <div class="card-body px-0 pt-0 pb-2">
        <div v-if="isLoading" class="text-center py-4">
          <i class="fas fa-spinner fa-spin me-1"></i>
          Cargando órdenes...
        </div>

        <p v-else-if="!orders.length" class="text-muted px-4 mb-0">
          No hay órdenes. Pulsa Sincronizar para traerlas desde Shopify o registra ventas desde el POS.
        </p>

        <div v-else class="table-responsive">
          <table class="table align-items-center mb-0">
            <thead>
              <tr>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-4" style="width: 56px">
                  <input
                    type="checkbox"
                    style="width: 16px; height: 16px; margin: 0; cursor: pointer; accent-color: #344767;"
                    title="Seleccionar todos"
                    :checked="allOrdersSelected"
                    :indeterminate.prop="someOrdersSelected"
                    @change="toggleSelectAllOrders"
                  />
                </th>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Orden
                </th>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Cliente
                </th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Pago
                </th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Estado
                </th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Items
                </th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Total
                </th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Fecha
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="order in orders" :key="`${order.source}-${order.id}`">
                <td class="text-center ps-4">
                  <div class="d-flex align-items-center justify-content-center" style="gap: 10px">
                    <input
                      type="checkbox"
                      style="width: 16px; height: 16px; margin: 0; cursor: pointer; accent-color: #344767; flex-shrink: 0"
                      title="Seleccionar para imprimir lista de empaque"
                      :checked="isOrderSelected(order)"
                      @change="toggleOrderSelection(order)"
                    />
                    <span
                      v-if="isOrderSynced(order)"
                      class="btn btn-link btn-sm p-0 m-0 text-success d-flex align-items-center justify-content-center"
                      title="Sincronizado con Shopify"
                      style="cursor:default; width: 16px; height: 16px; line-height: 1"
                    >
                      <i class="fas fa-check-circle"></i>
                    </span>
                    <button
                      v-else-if="canSyncOrder(order)"
                      type="button"
                      class="btn btn-link btn-sm p-0 m-0 text-secondary d-flex align-items-center justify-content-center"
                      :disabled="syncingOrderId === order.id"
                      title="Enviar pedido a Shopify"
                      style="width: 16px; height: 16px; line-height: 1"
                      @click="syncPosOrder(order)"
                    >
                      <i
                        class="fas"
                        :class="syncingOrderId === order.id ? 'fa-spinner fa-spin' : 'fa-sync'"
                      ></i>
                    </button>
                    <button
                      v-if="order.source === 'pos'"
                      type="button"
                      class="btn btn-link btn-sm p-0 m-0 text-info d-flex align-items-center justify-content-center"
                      title="Ver ticket de venta"
                      style="width: 16px; height: 16px; line-height: 1"
                      @click="regenerateTicket(order)"
                    >
                      <i class="fas fa-receipt"></i>
                    </button>
                    <button
                      type="button"
                      class="btn btn-link btn-sm p-0 m-0 text-secondary d-flex align-items-center justify-content-center"
                      title="Ver detalles del pedido"
                      style="width: 16px; height: 16px; line-height: 1"
                      @click="viewOrderDetail(order)"
                    >
                      <i class="fas fa-eye"></i>
                    </button>
                  </div>
                </td>
                <td>
                  <span class="text-sm font-weight-bold d-block">{{ orderLabel(order) }}</span>
                  <span class="text-xs text-secondary">{{ orderSubtitle(order) }}</span>
                </td>
                <td>
                  <span class="text-sm font-weight-bold d-block">{{ orderCustomerName(order) }}</span>
                  <span v-if="orderCustomerEmail(order)" class="text-xs text-secondary">{{ orderCustomerEmail(order) }}</span>
                </td>
                <td class="text-center">
                  <span class="badge badge-sm" :class="paymentBadgeClass(order)">
                    {{ orderPaymentMethod(order) }}
                  </span>
                </td>
                <td class="text-center">
                  <span class="badge badge-sm" :class="orderStatusBadgeClass(order)">
                    {{ currentOrderStatus(order) }}
                  </span>
                </td>
                <td class="text-center">
                  <span class="text-sm">{{ orderLineItemsCount(order) }}</span>
                </td>
                <td class="text-center">
                  <span class="text-sm">{{ formatMoney(orderTotal(order), orderCurrency(order)) }}</span>
                </td>
                <td class="text-center">
                  <span class="text-xs text-secondary">{{ formatDate(orderDate(order)) }}</span>
                </td>
              </tr>
            </tbody>
          </table>

          <div class="pagination p-3">
            <a
              v-for="link in links"
              :key="link.label"
              class="page-item cursor-pointer"
              :class="{ active: link.active, disabled: !link.url }"
              @click="link.url && fetchOrdersFromLink(link.url)"
            >
              {{ link.label }}
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de escaneo de código de barras -->
    <div v-if="isScanning" class="scanner-modal-overlay" @click.self="stopScanner">
      <div class="scanner-modal card shadow">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="mb-0">Escanear código de barras</h6>
            <button type="button" class="btn-close" @click="stopScanner"></button>
          </div>
          <div class="scanner-video-wrapper">
            <video ref="scannerVideo" autoplay playsinline></video>
          </div>
          <p class="text-xs text-muted text-center mt-2 mb-0">Apunta la cámara al código de barras del pedido</p>
        </div>
      </div>
    </div>

    <!-- Modal de impresión de código de barras -->
    <div v-if="showBarcodeModal" class="barcode-modal-overlay" @click.self="closeBarcodeModal">
      <div class="barcode-modal card shadow">
        <div class="card-body">
          <h6 class="mb-3">Generar códigos de barras</h6>

          <div v-if="!barcodeItems.length" class="alert alert-warning py-2 text-sm">
            No se pudieron obtener los números de orden.
          </div>

          <template v-else>
            <p class="text-xs text-muted mb-2">Vista previa de la etiqueta (100 x 50 mm) — {{ barcodeItems.length }} pedido(s)</p>
            <div class="barcode-preview-wrapper">
              <div class="barcode-label" ref="labelPreview">
                <div class="barcode-label-customer">{{ barcodeItems[0]?.customerName || 'Cliente' }}</div>
                <div v-if="barcodeItems[0]?.city" class="barcode-label-city">{{ barcodeItems[0].city }}</div>
                <div class="barcode-label-body">
                  <svg ref="previewSvg"></svg>
                </div>
                <div class="barcode-label-number">{{ barcodeItems[0]?.barcode }}</div>
                <div v-if="barcodeItems[0]?.date" class="barcode-label-date">{{ barcodeItems[0].date }}</div>
              </div>
            </div>
          </template>

          <div class="d-flex gap-2 justify-content-end mt-3">
            <button type="button" class="btn btn-secondary btn-sm" @click="closeBarcodeModal">Cancelar</button>
            <button
              v-if="barcodeItems.length"
              type="button"
              class="btn btn-success btn-sm"
              @click="printBarcodes"
            >
              <i class="fas fa-print me-1"></i> Imprimir
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import shopifyService from "@/services/shopify.service";
import Swal from "sweetalert2";
import JsBarcode from 'jsbarcode';
import { BrowserMultiFormatReader } from '@zxing/browser';

export default {
  name: "ShopifyOrders",
  components: {
  },
  data() {
    return {
      isSyncing: false,
      isLoading: false,
      isPrintingPickingList: false,
      selectedOrders: [],
      syncingOrderId: null,
      syncProgress: { total: 0, created: 0, updated: 0 },
      updatingStatusKey: null,
      statusOptions: [
        "NO PREPARADO",
        "ALISTANDO",
        "PREPARADO",
        "DESPACHADO",
        "ENTREGADO",
      ],
      orders: [],
      links: [],
      searchQuery: "",
      searchTimeout: null,
      statusFilter: "",
      paymentStatusFilter: "",
      dateFromFilter: "",
      dateToFilter: "",
      showBarcodeModal: false,
      barcodeItems: [],
      barcodeQuantity: 3,
      isScanning: false,
      isMobileDevice: false,
      scannerReader: null,
      scannerControls: null,
    };
  },
  computed: {
    allOrdersSelected() {
      return this.orders.length > 0 && this.orders.every((order) => this.isOrderSelected(order));
    },
    someOrdersSelected() {
      const selected = this.orders.filter((order) => this.isOrderSelected(order)).length;
      return selected > 0 && selected < this.orders.length;
    },
  },
  mounted() {
    this.fetchOrders();
    this.isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || (window.innerWidth <= 1024 && 'ontouchstart' in window);
  },
  beforeUnmount() {
    this.stopScanner();
  },
  methods: {
    clearFilters() {
      this.searchQuery = "";
      this.statusFilter = "";
      this.paymentStatusFilter = "";
      this.dateFromFilter = "";
      this.dateToFilter = "";
      this.fetchOrders(1);
    },
    onSearchInput() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.fetchOrders(1);
      }, 400);
    },
    onSearchEnter() {
      const value = this.searchQuery.trim();
      if (/^\d+$/.test(value)) {
        this.findAndRedirectToOrder(value);
      } else {
        this.fetchOrders(1);
      }
    },
    async findAndRedirectToOrder(orderNumber) {
      const num = String(orderNumber);

      // Primero buscar en la página actual por order_number o id
      // (los pedidos POS tienen order_number=NULL y se identifican por id)
      const matched = this.orders.find(
        (o) => String(o.order_number) === num || String(o.id) === num
      );
      if (matched) {
        this.viewOrderDetail(matched);
        return;
      }

      // Si no está en la página actual, hacer búsqueda al backend
      // y luego buscar coincidencia exacta en los resultados
      await this.fetchOrders(1);
      const exactMatch = this.orders.find(
        (o) => String(o.order_number) === num || String(o.id) === num
      );
      if (exactMatch) {
        this.viewOrderDetail(exactMatch);
      }
    },
    async toggleScanner() {
      if (this.isScanning) {
        this.stopScanner();
      } else {
        await this.startScanner();
      }
    },
    async startScanner() {
      this.isScanning = true;
      this.$nextTick(async () => {
        try {
          const videoElement = this.$refs.scannerVideo;
          if (!videoElement) return;

          this.scannerReader = new BrowserMultiFormatReader();

          this.scannerControls = await this.scannerReader.decodeFromVideoDevice(
            undefined,
            videoElement,
            (result) => {
              if (result) {
                const scannedValue = result.getText().trim();
                if (/^\d+$/.test(scannedValue)) {
                  this.stopScanner();
                  this.searchQuery = scannedValue;
                  this.findAndRedirectToOrder(scannedValue);
                }
              }
            }
          );
        } catch (error) {
          console.error('Error al iniciar el escáner:', error);
          Swal.fire('Error', 'No se pudo acceder a la cámara.', 'error');
          this.isScanning = false;
        }
      });
    },
    stopScanner() {
      if (this.scannerControls) {
        this.scannerControls.stop();
        this.scannerControls = null;
      }
      this.scannerReader = null;
      this.isScanning = false;
    },
    async fetchOrders(page = 1, { keepSelection = false } = {}) {
      this.isLoading = true;
      // Al cambiar de página (o recargar con filtros/búsqueda/sync) se limpia la
      // selección para no mantener checks en memoria de órdenes que ya no se ven.
      if (!keepSelection) {
        this.selectedOrders = [];
      }
      try {
        const response = await shopifyService.getOrders({
          page,
          search: this.searchQuery,
          status: this.statusFilter,
          payment_status: this.paymentStatusFilter,
          date_from: this.dateFromFilter,
          date_to: this.dateToFilter,
          per_page: 50,
        });
        const data = response?.data || {};

        this.orders = data.data || [];
        this.links = data.links || [];
      } catch (error) {
        console.error("Error al cargar órdenes Shopify:", error);
        await Swal.fire(
          "Error",
          error?.response?.data?.message || "No se pudieron cargar las órdenes.",
          "error"
        );
      } finally {
        this.isLoading = false;
      }
    },
    fetchOrdersFromLink(url) {
      const page = new URL(url).searchParams.get("page") || 1;
      this.fetchOrders(Number(page));
    },
    async syncOrders() {
      if (this.isSyncing) {
        return;
      }

      this.isSyncing = true;
      this.syncProgress = { total: 0, created: 0, updated: 0 };
      let pageInfo = null;

      try {
        do {
          const response = await shopifyService.syncOrders(pageInfo);
          const data = response?.data || {};

          if (!data.success) {
            throw new Error(data.message || "No se pudo sincronizar.");
          }

          const sync = data.sync || {};
          this.syncProgress.total += sync.total ?? 0;
          this.syncProgress.created += sync.created ?? 0;
          this.syncProgress.updated += sync.updated ?? 0;
          pageInfo = data.next_page_info || null;
        } while (pageInfo);

        await this.fetchOrders(1);
        await Swal.fire(
          "Sincronizado",
          `Se procesaron ${this.syncProgress.total} órdenes (${this.syncProgress.created} nuevas, ${this.syncProgress.updated} actualizadas).`,
          "success"
        );
      } catch (error) {
        console.error("Error al sincronizar órdenes Shopify:", error);
        await Swal.fire(
          "Error",
          error?.response?.data?.message || error.message || "Error al sincronizar órdenes con Shopify.",
          "error"
        );
      } finally {
        this.isSyncing = false;
      }
    },
    isOrderSelected(order) {
      return this.selectedOrders.some((item) => item.id === order.id);
    },
    toggleOrderSelection(order) {
      if (this.isOrderSelected(order)) {
        this.selectedOrders = this.selectedOrders.filter((item) => item.id !== order.id);
      } else {
        this.selectedOrders.push(order);
      }
    },
    toggleSelectAllOrders() {
      if (this.allOrdersSelected) {
        const ids = new Set(this.orders.map((o) => o.id));
        this.selectedOrders = this.selectedOrders.filter((o) => !ids.has(o.id));
      } else {
        const existingIds = new Set(this.selectedOrders.map((o) => o.id));
        const toAdd = this.orders.filter((o) => !existingIds.has(o.id));
        this.selectedOrders = [...this.selectedOrders, ...toAdd];
      }
    },
    async printPickingList() {
      if (!this.selectedOrders.length || this.isPrintingPickingList) {
        return;
      }

      this.isPrintingPickingList = true;

      try {
        const ids = this.selectedOrders.map((order) => order.id);
        const response = await shopifyService.getPickingListPdf(ids);

        const pdfBlob = new Blob([response.data], { type: "application/pdf" });
        const pdfUrl = URL.createObjectURL(pdfBlob);
        window.open(pdfUrl, "_blank");
      } catch (error) {
        console.error("Error al generar la lista de empaque:", error);
        await Swal.fire(
          "Error",
          error?.response?.data?.message || "No se pudo generar la lista de empaque.",
          "error"
        );
      } finally {
        this.isPrintingPickingList = false;
      }
    },
    isOrderSynced(order) {
      if (order?.source === "shopify" || order?.shopify_order_id) {
        return true;
      }

      return order?.shopify_product_id != null;
    },
    canSyncOrder(order) {
      return order?.source === "pos" && !this.isOrderSynced(order);
    },
    async syncPosOrder(order) {
      if (!this.canSyncOrder(order) || this.syncingOrderId !== null) {
        return;
      }

      const confirm = await Swal.fire({
        title: "Enviar a Shopify",
        text: `¿Deseas crear el pedido #${order.id} en Shopify?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Sí, sincronizar",
        cancelButtonText: "Cancelar",
      });

      if (!confirm.isConfirmed) {
        return;
      }

      this.syncingOrderId = order.id;

      try {
        const response = await shopifyService.syncPosSale(order.id);
        const data = response?.data || {};

        if (!data.success) {
          throw new Error(data.message || "No se pudo sincronizar la venta.");
        }

        await Swal.fire(
          "Sincronizado",
          `Pedido creado en Shopify: ${data.shopify_order_name || data.shopify_order_id}`,
          "success"
        );

        await this.fetchOrders();
      } catch (error) {
        await Swal.fire(
          "Error",
          error?.response?.data?.message || error.message || "No se pudo enviar el pedido a Shopify.",
          "error"
        );
      } finally {
        this.syncingOrderId = null;
      }
    },
    regenerateTicket(order) {
      if (order?.source !== 'pos') return;
      this.$router.push({ path: '/shopify/pos/ticket', query: { id: order.id } });
    },
    viewOrderDetail(order) {
      this.$router.push({
        name: 'Shopify Order Detail',
        query: { source: order.source || 'shopify', id: order.id },
      });
    },
    orderLabel(order) {
      if (order?.source === "shopify") {
        return order.title || `#${order.shopify_order_id}`;
      }

      return `#${order.id}`;
    },
    orderSubtitle(order) {
      if (order?.shopify_order_id != null) {
        return `P. SHOPIFY #${order.order_number ?? order.shopify_order_id}`;
      }

      if (order?.shopify_product_id) {
        return `Shopify #${order.shopify_product_id}`;
      }

      return order.title || "Venta POS";
    },
    orderFulfillmentLabel(order) {
      if (order?.source === "pos") {
        return this.orderDeliveryLabel(order);
      }

      return order?.fulfillment_status || "—";
    },
    orderDeliveryLabel(order) {
      const method = this.orderVariants(order).delivery_method || "";

      return (
        {
          envio_nacional: "ENVIO NACIONAL",
          envio_local: "ENVIO LOCAL",
          efectivo: "EFECTIVO",
          retiro: "RETIRO EN TIENDA",
          domicilio: "DOMICILIO",
        }[method] || method.toUpperCase() || "POS"
      );
    },
    orderStatusKey(order) {
      return `${order.source}-${order.id}`;
    },
    currentOrderStatus(order) {
      const history = Array.isArray(order?.status_history) ? order.status_history : [];

      if (!history.length) {
        if (order?.fulfillment_status === "fulfilled") {
          return "PREPARADO";
        }
        return "NO PREPARADO";
      }

      return history[history.length - 1]?.status || "NO PREPARADO";
    },
    async updateOrderStatus(order, status) {
      if (!order?.source || !order?.id || status === this.currentOrderStatus(order)) {
        return;
      }

      const key = this.orderStatusKey(order);
      const source = order.source;
      const id = order.id;
      this.updatingStatusKey = key;

      try {
        const response = await shopifyService.updateOrderStatus(source, id, status);
        const data = response?.data || {};

        if (!data.success) {
          throw new Error(data.message || "No se pudo actualizar el estado.");
        }

        if (data.status_history) {
          const orderIndex = this.orders.findIndex(
            (item) => item.source === source && String(item.id) === String(id)
          );

          if (orderIndex !== -1) {
            this.orders.splice(orderIndex, 1, {
              ...this.orders[orderIndex],
              status_history: data.status_history,
            });
          }
        }
      } catch (error) {
        await Swal.fire(
          "Error",
          error?.response?.data?.message || error.message || "No se pudo actualizar el estado del pedido.",
          "error"
        );
        await this.fetchOrders();
      } finally {
        this.updatingStatusKey = null;
      }
    },
    orderVariants(order) {
      return order?.variants || {};
    },
    orderCustomerName(order) {
      return this.orderVariants(order).customer_name || order?.customer_name || "—";
    },
    orderCustomerEmail(order) {
      return this.orderVariants(order).customer_email || order?.email || "";
    },
    orderPaymentMethod(order) {
      const rawMethod =
        order?.payment_method ||
        this.orderVariants(order).payment_method ||
        order?.financial_status ||
        "";
      
      const method = String(rawMethod).trim();
      const methodLower = method.toLowerCase();
      
      // Map Shopify gateway names to readable names
      const methodNames = {
        // Shopify gateway names
        "COD": "Contra Entrega",
        "addi payment": "ADDI",
        "epayco": "ePayco",
        "TRB: abona $20.000 y paga el resto al recibir": "Contra Entrega",
        "wompi": "WOMPI",
        "manual": "Efectivo",
        // Internal codes
        contra_entrega: "Contra Entrega",
        efectivo: "Efectivo",
        transferencia: "Transferencia Bancaria",
        tarjeta_credito: "Tarjeta de Crédito",
        tarjeta_debito: "Tarjeta de Débito",
        nequi: "Nequi",
        daviplata: "Daviplata",
        pse: "PSE",
        pending: "Pendiente",
        paid: "Pagado",
      };
      
      // Try exact match first, then lowercase match
      return methodNames[method] || 
             methodNames[methodLower] || 
             method.toUpperCase() || 
             "—";
    },
    orderLineItemsCount(order) {
      const items = this.orderVariants(order).line_items;
      if (Array.isArray(items)) return items.length;
      return order?.line_items_count ?? order?.variants_count ?? 0;
    },
    orderTotal(order) {
      return this.orderVariants(order).total ?? order?.total_price ?? order?.price_max;
    },
    orderDate(order) {
      return order?.shopify_created_at;
    },
    financialStatusClass(status) {
      if (status === "paid") return "bg-gradient-success";
      if (status === "pending" || status === "partially_paid") return "bg-gradient-warning";
      if (status === "refunded" || status === "voided") return "bg-gradient-secondary";
      return "bg-gradient-info";
    },
    paymentBadgeClass(order) {
      if (!this.isOrderSynced(order)) {
        return "bg-gradient-warning";
      }
      return this.financialStatusClass(order.financial_status);
    },
    fulfillmentStatusClass(status) {
      if (status === "pos") return "bg-gradient-secondary";
      if (status === "fulfilled") return "bg-gradient-success";
      if (status === "partial") return "bg-gradient-warning";
      if (!status) return "bg-gradient-secondary";
      return "bg-gradient-info";
    },
    orderStatusBadgeClass(order) {
      const status = this.currentOrderStatus(order);
      if (status === "PREPARADO") return "bg-gradient-success";
      if (status === "ALISTANDO") return "bg-gradient-info";
      if (status === "DESPACHADO") return "bg-gradient-primary";
      if (status === "ENTREGADO") return "bg-gradient-success";
      return "bg-gradient-secondary";
    },
    formatMoney(value, currency = "COP") {
      if (value == null) return "—";

      return new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: currency || "COP",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    },
    orderCurrency(order) {
      return order?.currency || "COP";
    },
    formatDate(value) {
      if (!value) return "—";

      return new Date(value).toLocaleString("es-CO", {
        dateStyle: "short",
        timeStyle: "short",
      });
    },
    openBarcodeModal() {
      const items = [];
      for (const order of this.selectedOrders) {
        const orderNumber = order.order_number || order.id;
        if (orderNumber) {
          const rawDate = this.orderDate(order);
          let dateStr = '';
          if (rawDate) {
            const d = new Date(rawDate);
            if (!isNaN(d.getTime())) {
              const dd = String(d.getDate()).padStart(2, '0');
              const mm = String(d.getMonth() + 1).padStart(2, '0');
              const yyyy = d.getFullYear();
              dateStr = `${dd}/${mm}/${yyyy}`;
            }
          }
          items.push({
            barcode: String(orderNumber).trim(),
            customerName: (order.customer_name || '').trim(),
            city: (order.shipping_address?.city || '').trim(),
            date: dateStr,
          });
        }
      }
      this.barcodeItems = items;
      this.showBarcodeModal = true;
      if (items.length) {
        this.$nextTick(() => this.renderPreviewBarcode());
      }
    },
    closeBarcodeModal() {
      this.showBarcodeModal = false;
    },
    async updateOrdersToAlistando(orders = []) {
      const toUpdate = orders.filter((order) => {
        const currentStatus = this.currentOrderStatus(order);
        return currentStatus === "NO PREPARADO" && order?.source && order?.id;
      });

      if (!toUpdate.length) return;

      try {
        const response = await shopifyService.batchUpdateOrderStatus(
          toUpdate.map((o) => ({ source: o.source, id: o.id })),
          "ALISTANDO"
        );
        const updated = response?.data?.updated || [];
        for (const item of updated) {
          const orderIndex = this.orders.findIndex(
            (o) => o.source === item.source && String(o.id) === String(item.id)
          );
          if (orderIndex !== -1 && item.status_history) {
            this.orders.splice(orderIndex, 1, {
              ...this.orders[orderIndex],
              status_history: item.status_history,
            });
          }
        }
        await this.fetchOrders(undefined, { keepSelection: true });
      } catch (error) {
        console.error('[updateOrdersToAlistando] Error:', error);
      }
    },
    renderPreviewBarcode() {
      if (!this.$refs.previewSvg || !this.barcodeItems.length) return;
      try {
        JsBarcode(this.$refs.previewSvg, this.barcodeItems[0].barcode, {
          format: 'CODE128',
          displayValue: true,
          fontSize: 10,
          height: 45,
          margin: 1,
          width: 2,
        });
      } catch (e) {
        console.error('Error generando código de barras:', e);
      }
    },
    async printBarcodes() {
      const realLabels = [];
      for (const item of this.barcodeItems) {
        const tmpSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        try {
          JsBarcode(tmpSvg, item.barcode, {
            format: 'CODE128',
            displayValue: false,
            fontSize: 10,
            height: 52,
            margin: 1,
            width: 2,
          });
        } catch (e) {
          console.error('Error generando código de barras:', e);
          continue;
        }
        const barcodeSvg = new XMLSerializer().serializeToString(tmpSvg);

        const labelHtml = `
          <div class="label">
            <div class="label-customer">${item.customerName || ''}</div>
            ${item.city ? `<div class="label-city">${item.city}</div>` : ''}
            <div class="label-body">${barcodeSvg}</div>
            <div class="label-number">${item.barcode}</div>
            ${item.date ? `<div class="label-date">${item.date}</div>` : ''}
          </div>`;

        realLabels.push(labelHtml);
      }

      // Rellenar con etiquetas vacías hasta completar un múltiplo de 3 columnas,
      // así la distribución simula siempre 3 columnas (1 pedido -> 1 real + 2 vacías,
      // 2 pedidos -> 2 reales + 1 vacía, etc.) y no se centra como un grupo pequeño.
      const COLUMNS = 3;
      const remainder = realLabels.length % COLUMNS;
      const placeholdersNeeded = remainder === 0 ? 0 : (COLUMNS - remainder);
      const placeholderHtml = '<div class="label label-placeholder"></div>';
      const labels = [...realLabels, ...Array(placeholdersNeeded).fill(placeholderHtml)];

      let rows = '';
      for (let i = 0; i < 1; i++) {
        rows += '<div class="label-row">' + labels.join('') + '</div>';
      }

      const html = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8" />
          <title>Imprimir códigos de barras</title>
          <style>
            @page { size: 104mm 25mm; margin: 0; }
            * { box-sizing: border-box; }
            html, body { margin: 0; padding: 0; }
            .label-row {
              display: flex;
              flex-direction: row;
              flex-wrap: wrap;
              justify-content: center;
              align-items: stretch;
              width: 104mm;
              page-break-after: always;
              margin-left:22px;
              margin-top:1px;
            }
            .label {
              width: 34mm;
              height: 25mm;
              padding: 2mm 3mm;
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              justify-content: center;
              font-family: Arial, Helvetica, sans-serif;
              overflow: hidden;
              text-align: left;
            }
            .label-placeholder {
              visibility: hidden;
            }
            .label-customer {
              font-size: 7pt;
              font-weight: bold;
              text-align: left;
              width: 100%;
              line-height: 1.2;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            .label-city {
              font-size: 7pt;
              font-weight: bold;
              text-align: left;
              width: 100%;
              line-height: 1.1;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            .label-date {
              font-size: 7pt;
              font-weight: normal;
              text-align: center;
              width: 100%;
              line-height: 1.1;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              margin-top: 1px;
            }
            .label-body {
              flex: 1;
              display: flex;
              align-items: center;
              justify-content: center;
              width: 100%;
            }
            .label-body svg {
              max-width: 70%;
              height: auto;
              max-height: 16mm;
              display: block;
              margin: 0 auto;
            }
            .label-number {
              font-size: 7pt;
              font-weight: bold;
              text-align: center;
              width: 100%;
            }
          </style>
        </head>
        <body onload="window.onafterprint = function(){ window.close(); }; setTimeout(function(){ window.print(); }, 100);">
          ${rows}
        </body>
        </html>`;

      const printWindow = window.open('', '_blank', 'width=800,height=600');
      if (!printWindow) {
        Swal.fire('Bloqueado', 'Permita las ventanas emergentes para imprimir.', 'warning');
        return;
      }
      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();

      const ordersToUpdate = [...this.selectedOrders];
      this.closeBarcodeModal();
      this.updateOrdersToAlistando(ordersToUpdate);
    },
  },
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.pagination {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 20px;
}

.page-item {
  margin: 0 5px;
  padding: 4px 8px;
  user-select: none;
}

.page-item.disabled {
  opacity: 0.5;
  pointer-events: none;
}

.active {
  font-weight: bold;
}

.order-status-select {
  min-width: 140px;
  font-size: 0.75rem;
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
}

.barcode-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1080;
}

.barcode-modal {
  width: 100%;
  max-width: 480px;
  margin: 1rem;
}

.barcode-preview-wrapper {
  display: flex;
  justify-content: center;
  background: #f6f7f9;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 10px;
}

.barcode-label {
  width: 100mm;
  height: 50mm;
  max-width: 100%;
  background: #fff;
  border: 1px solid #dee2e6;
  padding: 4mm;
  display: flex;
  flex-direction: column;
  transform-origin: top center;
}

.barcode-label-customer {
  font-size: 10pt;
  font-weight: bold;
  text-align: left;
  width: 100%;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 2px;
}

.barcode-label-city {
  font-size: 8pt;
  text-align: left;
  width: 100%;
  line-height: 1.1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #555;
  margin-bottom: 2px;
}

.barcode-label-date {
  font-size: 8pt;
  text-align: center;
  width: 100%;
  line-height: 1.1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #555;
  margin-top: 2px;
}

.barcode-label-number {
  font-size: 11pt;
  font-weight: bold;
  text-align: center;
  width: 100%;
  margin-top: 4px;
}

.barcode-label-body {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.barcode-label-body svg {
  max-width: 60mm;
  max-height: 30mm;
}

.scanner-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1080;
}

.scanner-modal {
  width: 100%;
  max-width: 400px;
  margin: 1rem;
}

.scanner-video-wrapper {
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  background: #000;
}

.scanner-video-wrapper video {
  width: 100%;
  height: auto;
  display: block;
}
</style>
